import React, {useState, useEffect, useRef, Fragment, useCallback, useMemo} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useLocation} from 'react-router-dom';
import {orderBy, cloneDeep} from 'lodash';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import TreeView from '@ey/canvascoreservices/TreeView';
import OutputText from '@ey/canvascoreservices/OutputText';

import {labels} from '../../../../util/utils';
import Utility from '../../../../util/utilityfunctions';
import {projectSessionKeys, routes, rootEntityId, rootLevelId} from '../../../../util/uiconstants';
import {loadStorage, saveStorage} from '../../../Common/localStorage';

import {getOrgChart} from '../../../../actions/Project/orgStructureAction';
import {setEditMode} from '../../../../actions/EditProject/editprojectactions';
import {updateTreeOrg, updateDisplayData} from '../../../../actions/Metrics/dashboardContextAction';
import {getEngagementMetaData} from '../../../../actions/Engagement/engagementactions';

import LoadingIndicator from '../../../Common/LoadingIndicator';
import StyledOrgChart from './StyledOrgChart';
import StyledFilters from './StyledFilters';
import AdvancedFiltersModal from '../AdvancedFilters/AdvancedFiltersModal';
import QuickFiltersDropdown from '../AdvancedFilters/QuickFiltersDropdown';

const OrgChart = ({projectId, useSession, onIsLoaded}) => {
	const selectedEngagements = useRef([]);
	const selectedEntities = useRef([]);
	const isInitialized = useRef(false);
	const [loading, setLoading] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [key, setKey] = useState(100);
	const location = useLocation();
	const isContactsPage = location?.pathname.toLowerCase().slice(1) == routes.contacts.root;
	const isRiskAssessmentPage = location?.pathname.toLowerCase().slice(1) == routes.gra.root;
	const project = useSelector(({project}) => project);

	const userPreferences = useSelector((store) => store.userPreferences);
	const isProjectOwner = project.users
		?.filter((user) => user.isProjectOwner)
		?.some((owner) => owner.userId === userPreferences.userId);
	const userId = userPreferences?.gui;

	const dispatch = useDispatch();

	const orgChart = useSelector((store) => store.orgChart);
	const entities = useSelector((store) => store.dashboardContext.entities);
	const userType = userPreferences.userType;
	const engagementMetaData = useSelector((store) => store.engagementMetaData);
	const isEditMode = useSelector((state) => state.isEditMode.activated);
	const isClassicProject = useSelector((state) => !state.project?.isGroupRiskAssessmentProject);
	const isGroupRiskAssessment = useSelector((store) => store.project.isGroupRiskAssessmentProject);

	const dashboardContextOrgChart = useSelector((store) => store.dashboardContext.orgChart);
	const projectSessionKey = `${projectSessionKeys.projectdetail}_${projectId}`;

	useEffect(() => {
		if (engagementMetaData === null) {
			dispatch(getEngagementMetaData(projectId, userType));
		}
	}, []);

	useEffect(() => {
		if (engagementMetaData !== null && isGroupRiskAssessment != null && project !== null) {
			dispatch(
				getOrgChart(
					projectId,
					useSession && {
						projectId: projectId,
						projectSessionKey: projectSessionKey,
						loadFromSession: true,
						isContactsPage,
						isGroupRiskAssessment,
						userType,
						engagementMetaData,
						isRiskAssessmentPage,
						isProjectOwner,
						userId
					}
				)
			).then(() => {
				setIsLoading(false);

				if (onIsLoaded) {
					onIsLoaded(false);
				}
			});
		}
	}, [engagementMetaData, isGroupRiskAssessment]);

	useEffect(() => {
		if (!_.isEmpty(orgChart)) {
			//If and API call is fired to update the value without a hard refresh, change the isInitialized approach.
			if (!isInitialized.current) {
				setIsLoading(true);
				if (isEditMode) {
					isEditModeFunctions();
				} else {
					const org = cloneDeep(orgChart);
					const selectedEntitiesId = loadStorage(projectSessionKey);
					updateSelectedEntities(org, true, selectedEntitiesId, true);
					let chart = getSortedEntities(org, isContactsPage);
					dispatch(updateTreeOrg(chart));
					isInitialized.current = true;
					setIsLoading(false);
				}
			} else {
				let chart = getSortedEntities(orgChart, isContactsPage);
				selectFromQuickFilters(
					orgChart.filter((o) => o.checked),
					chart
				);
			}
			setKey(key + 1);
		}
	}, [orgChart]);

	useEffect(() => {
		if (!_.isEmpty(orgChart)) {
			setLoading(false);
		}
	}, [entities]);

	const isEditModeFunctions = () => {
		dispatch(
			getOrgChart(
				projectId,
				useSession && {
					projectId: projectId,
					projectSessionKey: projectSessionKey,
					loadFromSession: true,
					isContactsPage,
					isGroupRiskAssessment,
					userType,
					engagementMetaData,
					isRiskAssessmentPage,
					isProjectOwner,
					userId
				}
			)
		).then(() => {
			dispatch(setEditMode(false, null, null, null)).then(() => {
				setIsLoading(false);
			});
		});
	};

	const addRemoveEngagements = (chart, ids, engagements, isChecked, selectedEntitiesId, onLoad) => {
		if (onLoad) {
			Array.prototype.push.apply(selectedEngagements.current, engagements);
			Array.prototype.push.apply(
				selectedEntities.current,
				chart.filter((i) => ids.includes(i.id))
			);

			dispatch(updateDisplayData(selectedEngagements.current, selectedEntities.current, dashboardContextOrgChart));
		} else {
			if (isChecked) {
				Array.prototype.push.apply(selectedEngagements.current, engagements);
				Array.prototype.push.apply(selectedEntities.current, chart);
				if (selectedEntitiesId && selectedEntitiesId.entitiesId) {
					selectedEntities.current = selectedEntities.current.filter((i) =>
						selectedEntitiesId.entitiesId.includes(i.id)
					);
				}
				dispatch(updateDisplayData(selectedEngagements.current, selectedEntities.current, dashboardContextOrgChart));
			} else {
				selectedEngagements.current = selectedEngagements.current.filter((e) => !engagements.includes(e));
				selectedEntities.current = selectedEntities.current.filter((i) => !ids.includes(i.id));
				dispatch(updateDisplayData(selectedEngagements.current, selectedEntities.current, dashboardContextOrgChart));
			}
		}
		if (useSession) {
			saveStorage(projectSessionKey, {
				entitiesId: _.map(selectedEntities.current, ({id}) => {
					return id;
				})
			});
		}
	};

	const selectFromQuickFilters = (checkedEntities, orgchart) => {
		let eng = [];
		for (let i = 0; i < checkedEntities.length; i++) {
			eng = eng.concat(checkedEntities[i].engagements);
		}
		selectedEntities.current = checkedEntities;
		selectedEngagements.current = eng;
		dispatch(updateTreeOrg(orgchart));
		dispatch(updateDisplayData(selectedEngagements.current, selectedEntities.current, dashboardContextOrgChart));
	};

	const updateSelectedEntities = (chart, isChecked, selectedEntitiesId, onLoad) => {
		let engs = [];
		let ids = [];
		for (let i = 0; i < chart.length; i++) {
			const entity = chart[i];
			if (selectedEntitiesId && selectedEntitiesId.entitiesId && !selectedEntitiesId.entitiesId.includes(entity.id)) {
				entity.checked = false;
			} else {
				if (onLoad) {
					if (
						entity.checked ||
						(selectedEntitiesId && selectedEntitiesId.entitiesId && selectedEntitiesId.entitiesId.includes(entity.id))
					) {
						Array.prototype.push.apply(engs, entity.engagements);
						ids.push(entity.id);
					}
				} else {
					Array.prototype.push.apply(engs, entity.engagements);
					entity.checked = isChecked;
					ids.push(entity.id);
				}
			}
		}
		addRemoveEngagements(chart, ids, engs, isChecked, selectedEntitiesId, onLoad);
	};

	const getDescendants = (entity, entities) => {
		let enabledEntities = [];
		const isRootEntity = entity.id === rootEntityId && entity.entityLevel === rootLevelId;
		if (entity.isEnabled || isRootEntity) {
			enabledEntities = [...entities, entity];
		} else {
			enabledEntities = [...entities];
		}

		if (entity.current && entity.current.data) {
			const children = Object.values(entity.current.data);
			for (let m = 0; m < children.length; m++) {
				const element = children[m];
				enabledEntities = getDescendants(element, enabledEntities);
			}
		}

		return enabledEntities;
	};

	const entityComponent = (props) => (
		<>
			<span className="engagement-label" title={props.label}>
				{props.label}
			</span>
			{props.accessGroups?.length > 0 && isClassicProject && (
				<span className="engagement-count">{props.engagements.length}</span>
			)}
		</>
	);

	const getSortedEntities = (entities, includeContacts) => {
		let result = [];
		let level = 1;
		let grouped = _.groupBy(_.sortBy(entities, ['entityLevel', 'label']), 'entityLevel');

		if (grouped[level]) {
			grouped[level].forEach((group) => {
				let node = {
					id: group.id,
					entityId: group.entityId,
					entityLevel: level,
					entityAdmins: group?.entityAdmins ? [...group.entityAdmins] : [],
					label: group.label,
					isEnabled: group.isEnabled,
					engagements: group.engagements,
					nodes: []
				};

				if (includeContacts) node.contacts = group.contacts;

				loadChildNode(node, level + 1, grouped, includeContacts);

				result.push(node);
			});
		}

		return cloneDeep(result);
	};

	const loadChildNode = (parentNode, level, groupedNodes, includeContacts) => {
		if (groupedNodes[level]) {
			// find child node for current node level
			let groups = groupedNodes[level].filter((x) => parentNode.id === x.parentId);

			groups.forEach((group) => {
				let childNode = {
					id: group.id,
					entityId: group.entityId,
					entityLevel: level,
					entityAdmins: group?.entityAdmins ? [...group.entityAdmins] : [],
					label: group.label,
					isEnabled: group.isEnabled,
					engagements: group.engagements,
					nodes: []
				};

				if (includeContacts) childNode.contacts = group.contacts;

				loadChildNode(childNode, level + 1, groupedNodes, includeContacts);

				parentNode.nodes.push(childNode);
			});
		}
	};

	const handlerOnFilterSearch = (entity, searchText) => Utility.isTextIncluded(entity.label, searchText);

	const handlerOnEntityChange = (entity, isChecked) => {
		setLoading(true);
		//Tree view expects a promise.
		const promise = new Promise((resolve, reject) => {
			entity.bulkChange = false;
			setTimeout(() => {
				const ens = getDescendants(entity, [], []);
				updateSelectedEntities(ens, isChecked, [], false);
			}, 300);
			resolve(true);
		});

		return promise;
	};

	const _getChilds = useCallback(
		(_, __, ___, parentItem, level) =>
			orgChart?.filter((item) => item.entityLevel === level && item.parentId === parentItem.id),
		[orgChart]
	);

	const treeView = useMemo(() => {
		const configuration = {
			data: orgChart?.filter((e) => e.entityLevel === 1),
			selected: (entity) => entity.checked,
			enabled: (entity) => entity.isEnabled,
			bulkChange: (entity) => entity.bulkChange,
			noResultsLabel: labels.noRecord,
			render: entityComponent,
			filter: handlerOnFilterSearch,
			onCheckBoxChange: handlerOnEntityChange,
			mapResponse: (response) =>
				response.map((item) => ({
					...item,
					children: orgChart?.filter((child) => child.parentId === item.id)
				})),
			children: {
				data: (page, size, selected, parentItem) => _getChilds(page, size, selected, parentItem, 2),
				selected: (entity) => entity.checked,
				enabled: (entity) => entity.isEnabled,
				bulkChange: (entity) => entity.bulkChange,
				noResultsLabel: labels.noRecord,
				render: entityComponent,
				filter: handlerOnFilterSearch,
				onCheckBoxChange: handlerOnEntityChange,
				orderBy: (response) => orderBy(response, (entity) => `${entity.label}`),
				mapResponse: (response) =>
					response.map((item) => ({
						...item,
						children: orgChart?.filter((child) => child.parentId === item.id)
					})),
				children: {
					data: (page, size, selected, parentItem) => _getChilds(page, size, selected, parentItem, 3),
					selected: (entity) => entity.checked,
					enabled: (entity) => entity.isEnabled,
					bulkChange: (entity) => entity.bulkChange,
					render: entityComponent,
					filter: handlerOnFilterSearch,
					onCheckBoxChange: handlerOnEntityChange,
					orderBy: (response) => orderBy(response, (entity) => `${entity.label}`),
					mapResponse: (response) =>
						response.map((item) => ({
							...item,
							children: orgChart?.filter((child) => child.parentId === item.id)
						})),
					children: {
						data: (page, size, selected, parentItem) => _getChilds(page, size, selected, parentItem, 4),
						selected: (entity) => entity.checked,
						enabled: (entity) => entity.isEnabled,
						bulkChange: (entity) => entity.bulkChange,
						render: entityComponent,
						filter: handlerOnFilterSearch,
						onCheckBoxChange: handlerOnEntityChange,
						orderBy: (response) => orderBy(response, (entity) => `${entity.label}`),
						mapResponse: (response) =>
							response.map((item) => ({
								...item,
								children: orgChart?.filter((child) => child.parentId === item.id)
							}))
					}
				}
			}
		};
		return (
			<section key={key}>
				<TreeView
					stickyRoot
					pagination={false}
					showSelectedOnlyCheckBox={false}
					expandText={labels.expand}
					searchHoverText={labels.search}
					collapseText={labels.collapse}
					searchClearHoverText={labels.clearHoverText}
					searchPlaceholder={labels.searchEntitiesPlaceholder}
					configuration={configuration}
					theme={Utility.getMotifTheme()}
				/>
			</section>
		);
	}, [orgChart, key]);

	return (
		<>
			{isLoading && <LoadingIndicator show styleName="inlineView" />}
			{!isLoading && (
				<>
					<OutputText>{labels.filters}</OutputText>
					<StyledFilters>
						<AdvancedFiltersModal />
						<QuickFiltersDropdown projectId={projectId} />
					</StyledFilters>
					<StyledOrgChart className={classNames({loading})}>{treeView}</StyledOrgChart>
				</>
			)}
		</>
	);
};

OrgChart.defaultProps = {
	projectId: '',
	onIsLoaded: () => {}
};

OrgChart.propTypes = {
	projectId: PropTypes.string,
	useSession: PropTypes.bool,
	onIsLoaded: PropTypes.func
};

export default OrgChart;
